/**
 * Router and providers.
 *
 * Every routed page is wrapped in `ProtectedRoute` with the SAME permission key the
 * corresponding Express route enforces, so the client-side hide and the server-side
 * refusal cannot drift apart. Where a page needs any one of several permissions (a
 * reports hub, say) `anyOf` is used rather than picking the narrowest.
 *
 * `ErrorBoundary` sits inside the shell, per page, so a rendering fault in one module
 * leaves the navigation usable.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { queryClient } from '@frontend/api/queryClient';
import { AuthProvider, useAuth } from '@frontend/auth/AuthProvider';
import { ProtectedRoute } from '@frontend/auth/ProtectedRoute';
import { AppShell } from '@frontend/layout/AppShell';
import { ErrorBoundary } from '@frontend/components/ui/ErrorBoundary';
import { LoadingRows } from '@frontend/components/ui/States';
import { ToastProvider } from '@frontend/components/ui/Toast';
import { Banner } from '@frontend/components/ui/Page';
import { LoginPage } from '@frontend/pages/Auth/Login';
import { Dashboard } from '@frontend/pages/Dashboard/Dashboard';
import { NotFound } from '@frontend/pages/NotFound';

// Timesheets and leave are the highest-traffic screens and load eagerly.
import { TimesheetGridPage } from '@frontend/pages/Timesheets/TimesheetGrid';
import { TimesheetHistoryPage } from '@frontend/pages/Timesheets/TimesheetHistory';
import { TimesheetApprovalsPage } from '@frontend/pages/Timesheets/TimesheetApprovals';
import { PayrollLockPage } from '@frontend/pages/Timesheets/PayrollLock';
import { MyLeavePage } from '@frontend/pages/Leave/MyLeave';
import { LeaveRequestPage } from '@frontend/pages/Leave/LeaveRequest';
import { LeaveApprovalsPage } from '@frontend/pages/Leave/LeaveApprovals';
import { TeamCalendarPage } from '@frontend/pages/Leave/TeamCalendar';
import { HolidaysPage } from '@frontend/pages/Leave/Holidays';
import { ApprovalInbox } from '@frontend/pages/Approvals/ApprovalInbox';

// Everything else is code-split: an employee who only logs time never downloads the
// finance or payroll bundles.
const UtilisationReport = lazy(() =>
  import('@frontend/pages/Timesheets/UtilisationReport').then((m) => ({ default: m.UtilisationReportPage })),
);
const EmployeeList = lazy(() =>
  import('@frontend/pages/Employees/EmployeeList').then((m) => ({ default: m.EmployeeListPage })),
);
const EmployeeDetail = lazy(() =>
  import('@frontend/pages/Employees/EmployeeDetail').then((m) => ({ default: m.EmployeeDetailPage })),
);
const EmployeeCreate = lazy(() =>
  import('@frontend/pages/Employees/EmployeeForm').then((m) => ({ default: m.EmployeeCreatePage })),
);
const EmployeeEdit = lazy(() =>
  import('@frontend/pages/Employees/EmployeeForm').then((m) => ({ default: m.EmployeeEditPage })),
);
const OrgChart = lazy(() =>
  import('@frontend/pages/Employees/OrgChart').then((m) => ({ default: m.OrgChartPage })),
);
const AgencyList = lazy(() =>
  import('@frontend/pages/Agencies/AgencyList').then((m) => ({ default: m.AgencyListPage })),
);
const AgencyDetail = lazy(() =>
  import('@frontend/pages/Agencies/AgencyDetail').then((m) => ({ default: m.AgencyDetailPage })),
);
const AgencyCreate = lazy(() =>
  import('@frontend/pages/Agencies/AgencyForm').then((m) => ({ default: m.AgencyCreatePage })),
);
const AgencyEdit = lazy(() =>
  import('@frontend/pages/Agencies/AgencyForm').then((m) => ({ default: m.AgencyEditPage })),
);
const PlacementList = lazy(() =>
  import('@frontend/pages/Agencies/PlacementList').then((m) => ({ default: m.PlacementListPage })),
);
const WorkAuthDashboard = lazy(() =>
  import('@frontend/pages/WorkAuth/WorkAuthDashboard').then((m) => ({ default: m.WorkAuthDashboardPage })),
);
const WorkAuthForm = lazy(() =>
  import('@frontend/pages/WorkAuth/WorkAuthForm').then((m) => ({ default: m.WorkAuthFormPage })),
);
const MyChecklist = lazy(() =>
  import('@frontend/pages/Onboarding/MyChecklist').then((m) => ({ default: m.MyChecklistPage })),
);
const ActiveChecklists = lazy(() =>
  import('@frontend/pages/Onboarding/ActiveChecklists').then((m) => ({ default: m.ActiveChecklistsPage })),
);
const ChecklistDetail = lazy(() =>
  import('@frontend/pages/Onboarding/ChecklistDetail').then((m) => ({ default: m.ChecklistDetailPage })),
);
const OnboardingTemplates = lazy(() =>
  import('@frontend/pages/Onboarding/Templates').then((m) => ({ default: m.OnboardingTemplatesPage })),
);
const ProjectList = lazy(() =>
  import('@frontend/pages/Projects/ProjectList').then((m) => ({ default: m.ProjectListPage })),
);
const ProjectDetail = lazy(() =>
  import('@frontend/pages/Projects/ProjectDetail').then((m) => ({ default: m.ProjectDetailPage })),
);
const Profitability = lazy(() =>
  import('@frontend/pages/Projects/Profitability').then((m) => ({ default: m.ProfitabilityPage })),
);
const ProjectCreate = lazy(() =>
  import('@frontend/pages/Projects/ProjectForm').then((m) => ({ default: m.ProjectCreatePage })),
);
const ProjectEdit = lazy(() =>
  import('@frontend/pages/Projects/ProjectForm').then((m) => ({ default: m.ProjectEditPage })),
);
const ClientList = lazy(() =>
  import('@frontend/pages/Clients/ClientList').then((m) => ({ default: m.ClientListPage })),
);
const ClientDetail = lazy(() =>
  import('@frontend/pages/Clients/ClientDetail').then((m) => ({ default: m.ClientDetailPage })),
);
const ClientCreate = lazy(() =>
  import('@frontend/pages/Clients/ClientForm').then((m) => ({ default: m.ClientCreatePage })),
);
const ClientEdit = lazy(() =>
  import('@frontend/pages/Clients/ClientForm').then((m) => ({ default: m.ClientEditPage })),
);
const ExpenseList = lazy(() =>
  import('@frontend/pages/Spending/ExpenseList').then((m) => ({ default: m.ExpenseListPage })),
);
const ExpenseDetail = lazy(() =>
  import('@frontend/pages/Spending/ExpenseDetail').then((m) => ({ default: m.ExpenseDetailPage })),
);
const ExpenseCreate = lazy(() =>
  import('@frontend/pages/Spending/ExpenseForm').then((m) => ({ default: m.ExpenseCreatePage })),
);
const VendorForm = lazy(() =>
  import('@frontend/pages/Spending/VendorForm').then((m) => ({ default: m.VendorFormPage })),
);
const PurchaseOrderForm = lazy(() =>
  import('@frontend/pages/Spending/PurchaseOrderForm').then((m) => ({ default: m.PurchaseOrderFormPage })),
);
const ExpenseApprovals = lazy(() =>
  import('@frontend/pages/Spending/ExpenseApprovals').then((m) => ({ default: m.ExpenseApprovalsPage })),
);
const SpendAnalytics = lazy(() =>
  import('@frontend/pages/Spending/SpendAnalytics').then((m) => ({ default: m.SpendAnalyticsPage })),
);
const PurchaseOrders = lazy(() =>
  import('@frontend/pages/Spending/PurchaseOrders').then((m) => ({ default: m.PurchaseOrdersPage })),
);
const Vendors = lazy(() =>
  import('@frontend/pages/Spending/Vendors').then((m) => ({ default: m.VendorsPage })),
);
const Invoices = lazy(() =>
  import('@frontend/pages/Finance/Invoices').then((m) => ({ default: m.InvoicesPage })),
);
const Ageing = lazy(() => import('@frontend/pages/Finance/Ageing').then((m) => ({ default: m.AgeingPage })));
const Bills = lazy(() => import('@frontend/pages/Finance/Bills').then((m) => ({ default: m.BillsPage })));
const Payments = lazy(() =>
  import('@frontend/pages/Finance/Payments').then((m) => ({ default: m.PaymentsPage })),
);
const Journal = lazy(() => import('@frontend/pages/Finance/Journal').then((m) => ({ default: m.JournalPage })));
const ChartOfAccounts = lazy(() =>
  import('@frontend/pages/Finance/ChartOfAccounts').then((m) => ({ default: m.ChartOfAccountsPage })),
);
const Periods = lazy(() => import('@frontend/pages/Finance/Periods').then((m) => ({ default: m.PeriodsPage })));
const Budgets = lazy(() => import('@frontend/pages/Finance/Budgets').then((m) => ({ default: m.BudgetsPage })));
const PayrollRuns = lazy(() =>
  import('@frontend/pages/Payroll/PayrollRuns').then((m) => ({ default: m.PayrollRunsPage })),
);
const PayrollRunDetail = lazy(() =>
  import('@frontend/pages/Payroll/PayrollRunDetail').then((m) => ({ default: m.PayrollRunDetailPage })),
);
const Payslips = lazy(() =>
  import('@frontend/pages/Payroll/Payslips').then((m) => ({ default: m.PayslipsPage })),
);
const ReportCatalog = lazy(() =>
  import('@frontend/pages/Reports/ReportCatalog').then((m) => ({ default: m.ReportCatalogPage })),
);
const FinancialStatement = lazy(() =>
  import('@frontend/pages/Reports/FinancialStatement').then((m) => ({ default: m.FinancialStatementPage })),
);
const NotificationInbox = lazy(() =>
  import('@frontend/pages/Notifications/NotificationInbox').then((m) => ({ default: m.NotificationInboxPage })),
);
const NotificationPreferences = lazy(() =>
  import('@frontend/pages/Notifications/NotificationPreferences').then((m) => ({
    default: m.NotificationPreferencesPage,
  })),
);
const AdminHome = lazy(() => import('@frontend/pages/Admin/AdminHome').then((m) => ({ default: m.AdminHome })));
const RolesAdmin = lazy(() => import('@frontend/pages/Admin/Roles').then((m) => ({ default: m.RolesPage })));
const PoliciesAdmin = lazy(() =>
  import('@frontend/pages/Admin/Policies').then((m) => ({ default: m.PoliciesPage })),
);
const LookupsAdmin = lazy(() =>
  import('@frontend/pages/Admin/Lookups').then((m) => ({ default: m.LookupsPage })),
);
const FormDesigner = lazy(() =>
  import('@frontend/pages/Admin/FormDesigner').then((m) => ({ default: m.FormDesignerPage })),
);
const SingleSignOnAdmin = lazy(() =>
  import('@frontend/pages/Admin/SingleSignOn').then((m) => ({ default: m.SingleSignOnPage })),
);
const DocumentStorageAdmin = lazy(() =>
  import('@frontend/pages/Admin/DocumentStorage').then((m) => ({ default: m.DocumentStoragePage })),
);
const EmailDeliveryAdmin = lazy(() =>
  import('@frontend/pages/Admin/EmailDelivery').then((m) => ({ default: m.EmailDeliveryPage })),
);
const ModulesAdmin = lazy(() =>
  import('@frontend/pages/Admin/Modules').then((m) => ({ default: m.ModulesPage })),
);
const JobCatalogue = lazy(() =>
  import('@frontend/pages/Admin/JobCatalogue').then((m) => ({ default: m.JobCataloguePage })),
);
const CareerPaths = lazy(() =>
  import('@frontend/pages/Admin/CareerPaths').then((m) => ({ default: m.CareerPathsPage })),
);
const JobArchitecture = lazy(() =>
  import('@frontend/pages/Admin/JobArchitecture').then((m) => ({ default: m.JobArchitecturePage })),
);
const PayBandMatrix = lazy(() =>
  import('@frontend/pages/Admin/PayBandMatrix').then((m) => ({ default: m.PayBandMatrixPage })),
);
const WorkflowTemplates = lazy(() =>
  import('@frontend/pages/Admin/WorkflowTemplates').then((m) => ({ default: m.WorkflowTemplatesPage })),
);
const NotificationTemplates = lazy(() =>
  import('@frontend/pages/Admin/NotificationTemplates').then((m) => ({ default: m.NotificationTemplatesPage })),
);
const RulesAdmin = lazy(() => import('@frontend/pages/Admin/Rules').then((m) => ({ default: m.RulesPage })));

function Page({ children, label }: { children: ReactNode; label?: string }): JSX.Element {
  return (
    <ErrorBoundary {...(label === undefined ? {} : { label })}>
      <Suspense fallback={<LoadingRows rows={6} columns={4} />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

/** Banner shown inside the shell when the session ends mid-use. */
function SessionNotice(): JSX.Element | null {
  const { sessionExpired } = useAuth();
  if (!sessionExpired) return null;
  return (
    <div className="fixed inset-x-0 top-0 z-banner p-2">
      <Banner tone="warning" title="Your session has ended">
        Sign in again to carry on.{' '}
        <a href="/login?reason=expired" className="font-medium text-content-link">
          Go to sign-in
        </a>
      </Banner>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <SessionNotice />
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Page label="The dashboard"><Dashboard /></Page>} />

                {/* ── Approvals ─────────────────────────────────────── */}
                <Route
                  path="approvals"
                  element={
                    <ProtectedRoute permission="workflows.instance.read">
                      <Page label="Approvals"><ApprovalInbox /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Timesheets ────────────────────────────────────── */}
                <Route
                  path="timesheets"
                  element={
                    <ProtectedRoute anyOf={['timesheets.read', 'timesheets.edit']}>
                      <Page label="Your timesheet"><TimesheetGridPage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="timesheets/history"
                  element={
                    <ProtectedRoute permission="timesheets.read">
                      <Page label="Timesheet history"><TimesheetHistoryPage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="timesheets/approvals"
                  element={
                    <ProtectedRoute permission="timesheets.approve">
                      <Page label="Timesheet approvals"><TimesheetApprovalsPage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="timesheets/payroll-lock"
                  element={
                    <ProtectedRoute permission="timesheets.lockPeriod">
                      <Page label="Payroll lock"><PayrollLockPage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="timesheets/reports"
                  /* `timesheets.report.read` is not in the permission catalogue, so this
                     page was unreachable for every non-super-admin. Utilisation is a view
                     of other people's hours: team scope or wider. */
                  element={
                    <ProtectedRoute permission="timesheets.read.team">
                      <Page label="Utilisation"><UtilisationReport /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Leave ─────────────────────────────────────────── */}
                <Route
                  path="leave"
                  element={
                    <ProtectedRoute permission="leave.request.read">
                      <Page label="Your leave"><MyLeavePage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="leave/request"
                  element={
                    <ProtectedRoute permission="leave.request.submit">
                      <Page label="Leave request"><LeaveRequestPage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="leave/approvals"
                  element={
                    <ProtectedRoute permission="leave.request.approve">
                      <Page label="Leave approvals"><LeaveApprovalsPage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="leave/calendar"
                  element={
                    <ProtectedRoute permission="leave.request.read">
                      <Page label="Team calendar"><TeamCalendarPage /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="leave/holidays"
                  element={
                    <ProtectedRoute permission="leave.holiday.read">
                      <Page label="Holidays"><HolidaysPage /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── People ────────────────────────────────────────── */}
                <Route
                  path="employees"
                  element={
                    <ProtectedRoute permission="hr.employee.read">
                      <Page label="The employee directory"><EmployeeList /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employees/org-chart"
                  element={
                    <ProtectedRoute permission="hr.orgChart.read">
                      <Page label="The org chart"><OrgChart /></Page>
                    </ProtectedRoute>
                  }
                />
                {/*
                  `new` and `:id/edit` are declared BEFORE `:employeeId`. React Router
                  matches in declaration order, so with the detail route first `/employees/new`
                  matched it and the app requested `GET /api/employees/new` — a 422 on the
                  primary call-to-action of the directory. The same ordering applies to
                  projects, clients, expenses, vendors and purchase orders below.
                */}
                <Route
                  path="employees/new"
                  element={
                    <ProtectedRoute permission="hr.employee.create">
                      <Page label="Add an employee"><EmployeeCreate /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employees/:employeeId/edit"
                  element={
                    <ProtectedRoute permission="hr.employee.update">
                      <Page label="Edit employee"><EmployeeEdit /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employees/:employeeId"
                  element={
                    <ProtectedRoute permission="hr.employee.read">
                      <Page label="The employee record"><EmployeeDetail /></Page>
                    </ProtectedRoute>
                  }
                />

                {/*
                  Agencies. `new` and `:id/edit` before `:agencyId`, for the same reason the
                  employee routes are ordered that way — React Router matches in declaration
                  order and `/agencies/new` would otherwise be read as an agency id.
                */}
                <Route
                  path="agencies"
                  element={
                    <ProtectedRoute permission="hr.agency.read">
                      <Page label="Staffing agencies"><AgencyList /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="agencies/placements"
                  element={
                    <ProtectedRoute permission="hr.agency.read">
                      <Page label="Agency placements"><PlacementList /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="agencies/new"
                  element={
                    <ProtectedRoute permission="hr.agency.manage">
                      <Page label="New agency"><AgencyCreate /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="agencies/:agencyId/edit"
                  element={
                    <ProtectedRoute permission="hr.agency.manage">
                      <Page label="Edit agency"><AgencyEdit /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="agencies/:agencyId"
                  element={
                    <ProtectedRoute permission="hr.agency.read">
                      <Page label="The agency record"><AgencyDetail /></Page>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="work-auth"
                  element={
                    <ProtectedRoute permission="workAuth.alert.read">
                      <Page label="Work authorisation"><WorkAuthDashboard /></Page>
                    </ProtectedRoute>
                  }
                />
                {/*
                  Nested under the employee because an authorisation only exists for one
                  person, and because this is the gate on staffing: nobody with no record
                  can be assigned to a project.
                */}
                <Route
                  path="employees/:employeeId/work-auth/new"
                  element={
                    <ProtectedRoute permission="workAuth.manage">
                      <Page label="Record work authorisation"><WorkAuthForm /></Page>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="onboarding"
                  element={
                    <ProtectedRoute permission="onboarding.checklist.read">
                      <Page label="Onboarding"><MyChecklist /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="onboarding/active"
                  element={
                    /* `.team`, matching the route behind it. The bare family key is satisfied
                       by `.own`, so a self-service account reached this screen and the API
                       served it every active checklist in the company. */
                    <ProtectedRoute permission="onboarding.checklist.read.team">
                      <Page label="Active onboarding"><ActiveChecklists kind="ONBOARDING" /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="onboarding/offboarding"
                  element={
                    /* Same screen, same route, same reason — and "who is leaving" is the
                       half of this data that gets repeated in a kitchen. */
                    <ProtectedRoute permission="onboarding.checklist.read.team">
                      <Page label="Offboarding"><ActiveChecklists kind="OFFBOARDING" /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="onboarding/templates"
                  element={
                    <ProtectedRoute permission="onboarding.template.read">
                      <Page label="Checklist templates"><OnboardingTemplates /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="onboarding/:instanceId"
                  element={
                    <ProtectedRoute permission="onboarding.checklist.read">
                      <Page label="The checklist"><ChecklistDetail /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Delivery ──────────────────────────────────────── */}
                <Route
                  path="projects"
                  element={
                    <ProtectedRoute permission="projects.read">
                      <Page label="Projects"><ProjectList /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="projects/profitability"
                  element={
                    <ProtectedRoute permission="projects.financials.read">
                      <Page label="Profitability"><Profitability /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="projects/new"
                  element={
                    <ProtectedRoute permission="projects.create">
                      <Page label="New project"><ProjectCreate /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="projects/:projectId/edit"
                  element={
                    <ProtectedRoute permission="projects.update">
                      <Page label="Edit project"><ProjectEdit /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="projects/:projectId"
                  element={
                    <ProtectedRoute permission="projects.read">
                      <Page label="The project"><ProjectDetail /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="clients"
                  element={
                    <ProtectedRoute permission="projects.client.read">
                      <Page label="Clients"><ClientList /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="clients/new"
                  element={
                    <ProtectedRoute permission="projects.client.manage">
                      <Page label="New client"><ClientCreate /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="clients/:clientId/edit"
                  element={
                    <ProtectedRoute permission="projects.client.manage">
                      <Page label="Edit client"><ClientEdit /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="clients/:clientId"
                  element={
                    <ProtectedRoute permission="projects.client.read">
                      <Page label="The client"><ClientDetail /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Spending ──────────────────────────────────────── */}
                <Route
                  path="expenses"
                  element={
                    <ProtectedRoute anyOf={['spending.expense.read', 'spending.expense.submit']}>
                      <Page label="Expenses"><ExpenseList /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="expenses/approvals"
                  element={
                    <ProtectedRoute permission="spending.expense.approve">
                      <Page label="Expense approvals"><ExpenseApprovals /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="expenses/analytics"
                  element={
                    <ProtectedRoute permission="spending.analytics.read">
                      <Page label="Spend analytics"><SpendAnalytics /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="expenses/new"
                  element={
                    <ProtectedRoute permission="spending.expense.submit">
                      <Page label="New expense claim"><ExpenseCreate /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="expenses/:expenseId"
                  element={
                    <ProtectedRoute permission="spending.expense.read">
                      <Page label="The expense claim"><ExpenseDetail /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="purchase-orders/new"
                  element={
                    <ProtectedRoute permission="spending.po.create">
                      <Page label="New purchase order"><PurchaseOrderForm /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="purchase-orders/:poId/edit"
                  element={
                    <ProtectedRoute permission="spending.po.create">
                      <Page label="Edit purchase order"><PurchaseOrderForm /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="purchase-orders"
                  element={
                    <ProtectedRoute permission="spending.po.read">
                      <Page label="Purchase orders"><PurchaseOrders /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="vendors/new"
                  element={
                    <ProtectedRoute permission="spending.vendor.manage">
                      <Page label="New vendor"><VendorForm /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="vendors/:vendorId/edit"
                  element={
                    <ProtectedRoute permission="spending.vendor.manage">
                      <Page label="Edit vendor"><VendorForm /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="vendors"
                  element={
                    <ProtectedRoute permission="spending.vendor.read">
                      <Page label="Vendors"><Vendors /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Finance ───────────────────────────────────────── */}
                <Route
                  path="finance/invoices"
                  element={
                    <ProtectedRoute permission="finance.invoice.read">
                      <Page label="Invoices"><Invoices /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/ar-ageing"
                  element={
                    <ProtectedRoute permission="finance.invoice.read">
                      <Page label="AR ageing"><Ageing kind="AR" /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/ap-ageing"
                  element={
                    <ProtectedRoute permission="finance.bill.read">
                      <Page label="AP ageing"><Ageing kind="AP" /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/bills"
                  element={
                    <ProtectedRoute permission="finance.bill.read">
                      <Page label="Bills"><Bills /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/payments"
                  element={
                    <ProtectedRoute permission="finance.payment.read">
                      <Page label="Payments"><Payments /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/journal"
                  element={
                    <ProtectedRoute permission="finance.journal.read">
                      <Page label="The journal"><Journal /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/accounts"
                  element={
                    <ProtectedRoute permission="finance.account.read">
                      <Page label="The chart of accounts"><ChartOfAccounts /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/periods"
                  element={
                    <ProtectedRoute permission="finance.period.close">
                      <Page label="Accounting periods"><Periods /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="finance/budgets"
                  element={
                    <ProtectedRoute permission="finance.budget.read">
                      <Page label="Budgets"><Budgets /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Payroll ───────────────────────────────────────── */}
                <Route
                  path="payroll"
                  element={
                    <ProtectedRoute permission="payroll.run.read">
                      <Page label="Payroll runs"><PayrollRuns /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="payroll/payslips"
                  element={
                    <ProtectedRoute permission="payroll.payslip.read">
                      <Page label="Payslips"><Payslips /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="payroll/:runId"
                  element={
                    <ProtectedRoute permission="payroll.run.read">
                      <Page label="The payroll run"><PayrollRunDetail /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Reports ───────────────────────────────────────── */}
                <Route
                  path="reports"
                  element={
                    <ProtectedRoute
                      anyOf={['finance.report.read', 'timesheets.read.team', 'spending.analytics.read']}
                    >
                      <Page label="Reports"><ReportCatalog /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reports/pl"
                  element={
                    <ProtectedRoute permission="finance.report.read">
                      <Page label="Profit and loss"><FinancialStatement kind="pl" /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reports/balance-sheet"
                  element={
                    <ProtectedRoute permission="finance.report.read">
                      <Page label="The balance sheet"><FinancialStatement kind="balance-sheet" /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reports/cash-flow"
                  element={
                    <ProtectedRoute permission="finance.report.read">
                      <Page label="Cash flow"><FinancialStatement kind="cash-flow" /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reports/trial-balance"
                  element={
                    <ProtectedRoute permission="finance.report.read">
                      <Page label="The trial balance"><FinancialStatement kind="trial-balance" /></Page>
                    </ProtectedRoute>
                  }
                />

                {/* ── Notifications ─────────────────────────────────── */}
                <Route
                  path="notifications"
                  element={<Page label="Notifications"><NotificationInbox /></Page>}
                />
                <Route
                  path="notifications/preferences"
                  element={<Page label="Notification settings"><NotificationPreferences /></Page>}
                />

                {/* ── Admin ─────────────────────────────────────────── */}
                <Route path="admin" element={<Page label="Administration"><AdminHome /></Page>} />
                <Route
                  path="admin/roles"
                  element={
                    <ProtectedRoute permission="admin.role.read">
                      <Page label="Roles and permissions"><RolesAdmin /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/policies"
                  element={
                    <ProtectedRoute permission="platform.policy.read">
                      <Page label="Policies"><PoliciesAdmin /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/lookups"
                  element={
                    <ProtectedRoute permission="platform.lookup.read">
                      <Page label="Lookups"><LookupsAdmin /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/forms"
                  element={
                    <ProtectedRoute permission="platform.formSchema.read">
                      <Page label="The form designer"><FormDesigner /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/modules"
                  element={
                    <ProtectedRoute permission="platform.navigation.read">
                      <Page label="Modules"><ModulesAdmin /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/sso"
                  element={
                    <ProtectedRoute permission="admin.settings.manage">
                      <Page label="Single sign-on"><SingleSignOnAdmin /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/email"
                  element={
                    <ProtectedRoute permission="admin.settings.manage">
                      <Page label="Email delivery"><EmailDeliveryAdmin /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/documents"
                  element={
                    <ProtectedRoute permission="onboarding.storage.manage">
                      <Page label="Document storage"><DocumentStorageAdmin /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/job-architecture"
                  element={
                    <ProtectedRoute permission="jobArchitecture.levelFramework.read">
                      <Page label="Job architecture"><JobArchitecture /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/job-architecture/catalogue"
                  element={
                    <ProtectedRoute permission="jobArchitecture.jobFamily.read">
                      <Page label="Job catalogue"><JobCatalogue /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/job-architecture/career-paths"
                  element={
                    <ProtectedRoute permission="jobArchitecture.careerPath.read">
                      <Page label="Career paths"><CareerPaths /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/job-architecture/pay-bands"
                  element={
                    <ProtectedRoute permission="jobArchitecture.payGrade.read">
                      <Page label="Pay bands"><PayBandMatrix /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/workflows"
                  element={
                    <ProtectedRoute permission="workflows.template.read">
                      <Page label="Workflows"><WorkflowTemplates /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/notification-templates"
                  element={
                    <ProtectedRoute permission="notifications.template.read">
                      <Page label="Notification templates"><NotificationTemplates /></Page>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/rules"
                  /* The catalogue key is `rules.read`; `rules.rule.read` never existed. */
                  element={
                    <ProtectedRoute permission="rules.read">
                      <Page label="Business rules"><RulesAdmin /></Page>
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<NotFound />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
